import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import {
  ChartComponentProps,
  getBaseOption,
  getChartNumericFields,
  getDisplayName,
  buildCategoryAxis,
  formatNumberShort,
  formatTooltipValue,
  getChartValueStats,
  formatTooltipStatsLine,
  ECHARTS_OPTS,
} from './chartConfig';
import { LineChart } from './LineChart';

type SeriesAssignment = {
  field: string;
  label: string;
  axis: 'primary' | 'secondary';
  marker: 'bar' | 'line';
  color?: string | null;
};

/**
 * Build per-series axis + marker assignments. Three input shapes,
 * tried in order:
 *
 * 1. ``chart.series[]`` from the BE chart_data_service. This is the
 *    canonical contract — each series carries explicit ``axis`` (primary
 *    / secondary) and ``type`` (bar / line / area).
 * 2. ``chart.secondary_y[]`` — an array of measure column names that
 *    should sit on the right axis. Combined with ``chart.type`` we
 *    infer markers (combo => bars on left, line on right).
 * 3. Numeric-field-half-split — legacy fallback. Splits available
 *    numeric fields evenly between the two axes; markers default to
 *    line (preserves the prior dual_axis_line behaviour).
 */
function buildSeriesAssignments(chart: ChartComponentProps['chart'], data: any[]): SeriesAssignment[] {
  const isCombo = chart.type === 'dual_axis_bar_line';
  if (Array.isArray(chart.series) && chart.series.length > 0) {
    return chart.series.map((s) => ({
      field: s.field,
      label: s.label || s.field,
      axis: s.axis === 'secondary' ? 'secondary' : 'primary',
      marker: s.type === 'bar' ? 'bar' : 'line',
      color: s.color ?? null,
    }));
  }

  const numericFields = getChartNumericFields(chart, data);
  if (numericFields.length === 0) return [];

  const secondarySet = new Set((chart.secondary_y || []).filter((f): f is string => typeof f === 'string'));
  if (secondarySet.size > 0) {
    return numericFields.map((field) => {
      const onSecondary = secondarySet.has(field);
      const marker: 'bar' | 'line' = isCombo ? (onSecondary ? 'line' : 'bar') : 'line';
      return {
        field,
        label: getDisplayName(chart, field),
        axis: onSecondary ? 'secondary' : 'primary',
        marker,
      };
    });
  }

  const halfIndex = Math.ceil(numericFields.length / 2);
  return numericFields.map((field, idx) => {
    const onSecondary = idx >= halfIndex;
    const marker: 'bar' | 'line' = isCombo ? (onSecondary ? 'line' : 'bar') : 'line';
    return {
      field,
      label: getDisplayName(chart, field),
      axis: onSecondary ? 'secondary' : 'primary',
      marker,
    };
  });
}

export const DualAxisLineChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors, presentation }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const assignments = buildSeriesAssignments(chart, data);
    if (assignments.length < 2) return {};

    const base = getBaseOption(colors);
    const palette = (colors && colors.length > 0 ? colors : base.color) as string[];

    const leftSeries = assignments.filter((a) => a.axis === 'primary');
    const rightSeries = assignments.filter((a) => a.axis === 'secondary');

    // Guard against degenerate inputs — if everything ended up on one
    // axis (e.g. one numeric field), fall back to a simple LineChart
    // upstream rather than rendering a broken dual-axis frame.
    if (leftSeries.length === 0 || rightSeries.length === 0) return {};

    const leftLabel = chart.y_axis_label || (leftSeries.length === 1 ? leftSeries[0].label : '');
    const rightLabel = rightSeries.length === 1 ? rightSeries[0].label : '';

    const makeValueAxis = (
      label: string,
      position: 'left' | 'right',
      axisColor: string,
    ): Record<string, unknown> => ({
      type: 'value',
      name: label,
      nameLocation: 'middle',
      nameGap: 55,
      nameTextStyle: { fontSize: 12, color: '#7B6B72' },
      position,
      axisLabel: { fontSize: 11, color: '#7B6B72', formatter: (v: number) => formatNumberShort(v) },
      axisLine: { show: true, lineStyle: { color: axisColor } },
      axisTick: { show: false },
      splitLine: { show: position === 'left', lineStyle: { color: '#EDE5D8', type: 'dashed' } },
    });

    const xAxis = { ...(buildCategoryAxis(chart, data) as Record<string, unknown>) };
    const leftAxisColor = leftSeries[0].color || palette[0] || '#6E3450';
    const rightAxisColor = rightSeries[0].color || palette[leftSeries.length] || '#4E7C89';
    const leftAxis = makeValueAxis(leftLabel, 'left', leftAxisColor);
    const rightAxis = makeValueAxis(rightLabel, 'right', rightAxisColor);

    if (presentation?.gridOverrides) {
      base.grid = {
        ...(base.grid as Record<string, unknown>),
        ...(presentation.gridOverrides as Record<string, unknown>),
      };
    }

    if (presentation?.hideXAxisLabels) {
      xAxis.axisLabel = { ...(xAxis.axisLabel as Record<string, unknown>), show: false };
      xAxis.axisTick = { ...(xAxis.axisTick as Record<string, unknown>), show: false };
      xAxis.name = presentation.hideXAxisName ? '' : xAxis.name;
    }

    if (presentation?.hideYAxisLabels) {
      leftAxis.axisLabel = { ...(leftAxis.axisLabel as Record<string, unknown>), show: false };
      leftAxis.axisTick = { ...(leftAxis.axisTick as Record<string, unknown>), show: false };
      leftAxis.name = presentation.hideYAxisName ? '' : leftAxis.name;
    }

    if (presentation?.hideRightYAxisLabels) {
      rightAxis.axisLabel = { ...(rightAxis.axisLabel as Record<string, unknown>), show: false };
      rightAxis.axisTick = { ...(rightAxis.axisTick as Record<string, unknown>), show: false };
      rightAxis.name = '';
    }

    const series: EChartsOption['series'] = assignments.map((assignment, i) => {
      const yAxisIndex = assignment.axis === 'secondary' ? 1 : 0;
      const seriesColor = assignment.color || palette[i % palette.length] || undefined;
      if (assignment.marker === 'bar') {
        return {
          name: assignment.label,
          type: 'bar' as const,
          yAxisIndex,
          data: data.map(d => d[assignment.field] ?? d.value ?? 0),
          itemStyle: seriesColor ? { color: seriesColor } : undefined,
          barMaxWidth: 36,
          emphasis: { focus: 'series' as const },
        };
      }
      return {
        name: assignment.label,
        type: 'line' as const,
        yAxisIndex,
        data: data.map(d => d[assignment.field] ?? d.value ?? 0),
        smooth: true,
        symbol: assignment.axis === 'secondary' ? 'diamond' : 'circle',
        symbolSize: 5,
        showSymbol: data.length <= 30,
        lineStyle: assignment.axis === 'secondary' ? { type: 'dashed' as const, color: seriesColor } : (seriesColor ? { color: seriesColor } : undefined),
        itemStyle: seriesColor ? { color: seriesColor } : undefined,
        emphasis: { focus: 'series' as const },
      };
    });

    const allFields = assignments.map((a) => a.field);

    return {
      ...base,
      grid: { ...base.grid as object, right: 60 },
      xAxis,
      yAxis: [leftAxis, rightAxis],
      series,
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'axis',
        formatter: (params: unknown) => {
          const items = Array.isArray(params) ? params : [params];
          const label = (items[0] as { name?: string })?.name ?? '';
          let html = `<div style="margin-bottom:4px;font-size:12px;color:#F7F3EE">${label}</div>`;
          for (const item of items) {
            const i = item as { marker?: string; seriesName?: string; value?: number };
            html += `<div style="display:flex;align-items:center;gap:6px;font-size:12px">${i.marker}<span>${i.seriesName}</span><span style="margin-left:auto">${formatTooltipValue(i.value)}</span></div>`;
          }
          const stats = getChartValueStats(data, allFields);
          html += `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(247,243,238,.22);font-size:11px;color:#D9CFC4">${formatTooltipStatsLine(stats)}</div>`;
          return html;
        },
      },
      legend: base.legend,
    };
  }, [chart, colors, presentation]);

  // Degrade gracefully — if we can't form a dual-axis frame, render
  // a plain LineChart instead of an empty canvas.
  const data = chart.data || [];
  if (data.length === 0) return null;
  const assignments = buildSeriesAssignments(chart, data);
  const leftCount = assignments.filter((a) => a.axis === 'primary').length;
  const rightCount = assignments.filter((a) => a.axis === 'secondary').length;
  if (leftCount === 0 || rightCount === 0) {
    return <LineChart chart={chart} height={height} colors={colors} presentation={presentation} />;
  }

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
